// Wizard de receta — Fase 5 (sección 2.8 del plan de fases), sub-flujo de
// prescripciones del formulario 005 (fila 4 de 8.3: "Evolución y
// Prescripciones"). Mismo patrón que Formulario024Consentimiento.tsx: un
// wizard de varios pasos con firma electrónica entre medio, por eso NO pasa
// por el motor genérico ni por `EXTRACTORES` (ver el comentario de
// `construirReceta` en construirReceta.ts).
//
// Rediseño 2026-09-02 — pedido explícito del usuario: una receta puede
// llevar VARIOS medicamentos con una sola firma (antes: un wizard completo,
// con su propia firma, por cada medicamento). Investigado y confirmado con
// el texto oficial del AM 1124: el Art. 5 permite "uno o más medicamentos...
// en la receta médica"; el "un solo medicamento" del Art. 7.II.10 describe
// el formulario FÍSICO propio de las unidades del MSP (Anexo 1), que este
// sistema no usa. Un `MedicationRequest` por medicamento (correcto en FHIR:
// cada orden es su propio recurso), todos con `supportingInformation`
// apuntando al MISMO `DocumentReference` firmado — un solo PDF, una sola
// firma, para toda la lista.
//
// Excepción dura: una sustancia controlada SIEMPRE va sola en su propia
// receta — nunca mezclada con medicamentos comunes (papel ACESS
// pre-numerado, sin QR propio, régimen totalmente distinto al de una
// receta común — no es parte de lo que el usuario pidió cambiar).
//
// También suma los campos que el AM 1124 Art. 7 exige y que la versión
// anterior no capturaba: número de receta, edad y sexo del paciente,
// diagnóstico presuntivo (CIE-10), y cantidad a dispensar "en números y
// letras" (Art. 7.II.10) — ver numeroALetras.ts.
//
// Puerto a shadcn/Tailwind de packages/hce/src/hce/recetas/RecetaWizard.tsx.
// No hay `Stepper` de shadcn — se reemplaza por un indicador de texto simple.
// El buscador de medicamento usa el mismo patrón Popover+Command que
// ValueSetCombobox.tsx, pero filtrando en memoria contra el catálogo local
// (buscarEnCatalogo) en vez de expandir un ValueSet de Medplum.
import { formatHumanName } from '@medplum/core';
import type { AllergyIntolerance, DocumentReference, Encounter, MedicationRequest, Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import { Loader2 } from 'lucide-react';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenantActual } from '@/auth/useTenantActual';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useRolDelPracticante } from '@/lib/useRolDelPracticante';
import type { DatosRecetaSinFirma, MedicamentoPdfReceta } from '@logiacare/hce-pdf';
import { emitirReceta, firmarDocumentoEnServidor, nuevoQrToken, prepararReceta } from '../../paciente/vaultClient';
import { EnvioDigitalDocumento } from '../EnvioDigitalDocumento';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { conCodigoCie } from '../discretos/camposSimplesDatos';
import { SelectorDiagnosticoCie10, type DiagnosticoCie10Valor } from '../discretos/SelectorDiagnosticoCie10';
import { construirReceta } from './construirReceta';
import { numeroALetras } from './numeroALetras';
import { recordarTelefono, telefonosRecientes } from './telefonosPrescriptorRecientes';
import { buscarEnCatalogo, marcaValida, marcasSugeridas, useCatalogoMedicamentos, type MedicamentoCatalogo } from './useCatalogoMedicamentos';

export interface RecetaWizardProps {
  readonly subject: Reference<Patient>;
  readonly encounter?: Reference<Encounter>;
  /** `vias` — la vía de administración de cada `mrs[i]` (no se guarda en el `MedicationRequest`; el documento firmado del 005 la necesita). */
  readonly onCreada: (mrs: MedicationRequest[], vias: string[]) => void;
  readonly onCancelar: () => void;
}

/** Vías de administración habituales (pedido de Sue, 2026-09-15) — lista cerrada ofrecida vía `<datalist>`, el campo sigue siendo texto libre para lo que falte acá. */
const VIAS_ADMINISTRACION_SUGERIDAS = [
  'Oral',
  'Sublingual',
  'Bucal',
  'Intravenosa',
  'Intramuscular',
  'Subcutánea',
  'Tópica',
  'Transdérmica',
  'Intradérmica',
  'Oftálmica',
  'Ótica',
  'Nasal',
  'Inhalatoria',
  'Rectal',
  'Vaginal',
  'Intraarticular',
  'Epidural',
  'Intratecal',
] as const;

/** Unidades de dosis habituales (pedido de Sue, 2026-09-15) — mismo criterio que las vías: `<datalist>` sobre un campo de texto libre. */
const UNIDADES_DOSIS_SUGERIDAS = [
  'tableta',
  'cápsula',
  'mg',
  'g',
  'mL',
  'gota(s)',
  'aplicación',
  'puff/inhalación',
  'ampolla',
  'sobre',
  'supositorio',
  'óvulo',
  'parche',
  'UI',
] as const;

/**
 * Vigencia máxima de la receta (2026-09-14, pedido explícito de Sue, texto
 * legal citado tal cual) — antes era un campo de texto libre con "30 días"
 * precargado, un valor que no corresponde a NINGUNA de estas 4 categorías
 * reales. Ahora es un desplegable cerrado: el médico elige la categoría, no
 * redacta el texto.
 */
// `texto` es la opción legible del desplegable (con la categoría legal);
// `pdfTexto` es lo que pide Sue que salga impreso en la receta — solo el
// numeral, ej. "Vigencia: (03) días" (la etiqueta "Vigencia:" ya la agrega
// receta.ts, ver DatosPdfReceta.vigencia).
const OPCIONES_VIGENCIA_RECETA = [
  { id: 'emergencia', texto: 'Área de emergencia: 1 día desde la fecha de prescripción', pdfTexto: '(01) día' },
  { id: 'ambulatoria', texto: 'Atención ambulatoria o consulta externa: 3 días desde la fecha de prescripción', pdfTexto: '(03) días' },
  { id: 'hospitalizacion', texto: 'Hospitalización: 1 día desde la fecha de prescripción', pdfTexto: '(01) día' },
  { id: 'antimicrobianos', texto: 'Medicamentos antimicrobianos: 3 días desde la fecha de prescripción', pdfTexto: '(03) días' },
] as const;

interface DatosClinicos {
  dci: string;
  nombreComercial: string;
  formaFarmaceutica: string;
  concentracion: string;
  /** "Oral", "Intravenosa", "Intramuscular", "Tópica"... — checklist MSP/ACESS, campo propio (no se infiere de `formaFarmaceutica`). */
  viaAdministracion: string;
  dosisCantidad: string;
  dosisUnidad: string;
  frecuenciaHoras: string;
  duracionDias: string;
  cantidadTotal: string;
  sustanciaControlada: boolean;
  /**
   * Candado duro (pedido explícito de Sue, 2026-09-17): true cuando este
   * ítem viene del catálogo ARCSA con `formaVenta` "...controlada" — en ese
   * caso `sustanciaControlada` queda forzada y el checkbox se deshabilita
   * (ver el render más abajo y `actualizarDatos`), el médico ya no decide.
   * `false` en entrada manual (`empezarManual`, sin catálogo) o cuando el
   * catálogo no dice "controlada": ahí el checkbox sigue siendo enteramente
   * editable, el médico puede necesitarlo por otras razones.
   */
  controladaBloqueadaPorCatalogo: boolean;
  numeroSerieRecetaEspecial: string;
}

export function datosDeCatalogo(m: MedicamentoCatalogo): DatosClinicos {
  const bloqueada = m.formaVenta.toLowerCase().includes('controlada');
  return {
    dci: m.dci,
    nombreComercial: marcaValida(m.marca) ? m.marca : '',
    formaFarmaceutica: m.forma,
    concentracion: m.concentracion,
    viaAdministracion: '',
    dosisCantidad: '',
    dosisUnidad: '',
    frecuenciaHoras: '',
    duracionDias: '',
    cantidadTotal: '',
    // El catálogo ARCSA manda: si dice "controlada", queda fija y bloqueada (candado duro, ver `controladaBloqueadaPorCatalogo`).
    sustanciaControlada: bloqueada,
    controladaBloqueadaPorCatalogo: bloqueada,
    numeroSerieRecetaEspecial: '',
  };
}

function calcularCantidadTotal(d: Pick<DatosClinicos, 'dosisCantidad' | 'frecuenciaHoras' | 'duracionDias'>): string {
  const dosis = Number(d.dosisCantidad);
  const horas = Number(d.frecuenciaHoras);
  const dias = Number(d.duracionDias);
  if (!dosis || !horas || !dias) return '';
  const tomasPorDia = Math.ceil(24 / horas);
  return String(dosis * tomasPorDia * dias);
}

// Sin `d.concentracion`: el pedido del médico (Art. 5.c.iii de la Resolución
// ACESS-2023-0030) ya queda cubierto por la fuerza (mg) que trae el nombre
// del producto del catálogo ARCSA — el texto de composición completo
// (excipientes, porcentajes, a veces corrupto del propio registro de ARCSA)
// es ruido redundante, no un dato legal adicional. `concentracion` sigue
// siendo un campo editable del formulario (línea "Concentración" más abajo)
// para que el médico la revise al armar la receta, solo que ya no se
// imprime ni se guarda en el nombre del medicamento.
function nombreMedicamentoDe(d: DatosClinicos): string {
  return [d.dci.trim(), d.nombreComercial.trim() ? `(${d.nombreComercial.trim()})` : '', d.formaFarmaceutica.trim()].filter(Boolean).join(' ');
}

function pautaDe(
  d: Pick<DatosClinicos, 'dosisCantidad' | 'dosisUnidad' | 'frecuenciaHoras' | 'duracionDias'>,
  t: (k: string, o?: Record<string, string>) => string,
): string | undefined {
  if (!d.dosisCantidad || !d.dosisUnidad || !d.frecuenciaHoras || !d.duracionDias) return undefined;
  return t('receta.pautaTexto', { cantidad: d.dosisCantidad, unidad: d.dosisUnidad, horas: d.frecuenciaHoras, dias: d.duracionDias });
}

function edadEnAniosYMeses(birthDate: string | undefined): string | undefined {
  if (!birthDate) return undefined;
  const nacimiento = new Date(birthDate);
  const ahora = new Date();
  let anios = ahora.getFullYear() - nacimiento.getFullYear();
  let meses = ahora.getMonth() - nacimiento.getMonth();
  if (ahora.getDate() < nacimiento.getDate()) meses--;
  if (meses < 0) {
    anios--;
    meses += 12;
  }
  return `${anios} años, ${meses} meses`;
}

type Estado =
  | { fase: 'carrito' }
  | { fase: 'editando-item'; datos: DatosClinicos; editIndex: number | null; cantidadEditadaAMano: boolean; error: string | null }
  | { fase: 'firma-profesional' }
  | { fase: 'procesando'; mensaje: string }
  /** Mismo patrón que PanelFirmaImpresion.tsx (ver/imprimir + descargar) — antes la receta cerraba el wizard apenas firmaba, sin ofrecer ver el PDF firmado (único formulario del catálogo sin este paso, pedido explícito del usuario 2026-09-03). `pdf`/`link` sostienen el botón "Envío digital" (Fase 4). */
  | { fase: 'listo'; url: string; pdf: Uint8Array; link: string | undefined; simulada: boolean; mrs: MedicationRequest[]; vias: string[] }
  | { fase: 'error'; mensaje: string };

function pasoActivo(fase: Estado['fase']): number {
  if (fase === 'carrito') return 0;
  if (fase === 'editando-item') return 1;
  return 2;
}

export function RecetaWizard({ subject, encounter, onCreada, onCancelar }: RecetaWizardProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  // Clave del recordatorio de teléfono del prescriptor (telefonosPrescriptorRecientes.ts)
  // — por practicante logueado, no por navegador/equipo, para no mezclar el
  // teléfono de un médico con el de otro en un equipo compartido.
  const practitionerId = medplum.getProfile()?.id ?? '';
  const catalogoEstado = useCatalogoMedicamentos();
  const tenantActual = useTenantActual();
  const [items, setItems] = useState<DatosClinicos[]>([]);
  const [diagnostico, setDiagnostico] = useState<DiagnosticoCie10Valor>({ texto: '', cie10: null });
  const [estado, setEstado] = useState<Estado>({ fase: 'carrito' });
  const [busqueda, setBusqueda] = useState('');
  // "Ver/imprimir" (2026-09-14): un <a target="_blank"> a una URL blob: no
  // abre nada en la mayoría de navegadores (Chrome bloquea la navegación de
  // una pestaña nueva a blob: sin relación con quien la creó), y
  // window.open() de reemplazo también lo bloquean varios bloqueadores de
  // popups — confirmado en vivo. La única forma sin bloqueo posible es
  // mostrar el PDF EN la misma página (un <iframe>, con su propio botón de
  // imprimir nativo del visor de PDF del navegador), nunca una ventana nueva.
  const [mostrarPreview, setMostrarPreview] = useState(false);
  // Registro MSP (2026-09-12): lo decide el servidor (hce_users.matricula,
  // vía /api/hce/mi-rol), solo lectura — /firmar-documento y /emitir-receta ignoran el
  // que mande el navegador. Sin matrícula cargada no se puede firmar.
  const rolEstado = useRolDelPracticante();
  const registroMsp = rolEstado.cargando ? '' : (rolEstado.matricula ?? '');
  /** Checklist MSP/ACESS: 4 categorías legales fijas (ver OPCIONES_VIGENCIA_RECETA) — "ambulatoria" por defecto, la más común en un consultorio (no hospitalización/emergencia). */
  const [vigenciaId, setVigenciaId] = useState<(typeof OPCIONES_VIGENCIA_RECETA)[number]['id']>('ambulatoria');
  const vigencia = OPCIONES_VIGENCIA_RECETA.find((o) => o.id === vigenciaId)!.pdfTexto;
  /** Sección "Indicaciones" (Resolución ACESS-2023-0030, Art. 5.e) — los 3 opcionales, el médico decide si aplican para esta receta. */
  // Precargado con el último teléfono usado (telefonosPrescriptorRecientes.ts,
  // localStorage) — pedido explícito del usuario: que el sistema lo recuerde
  // en toda ficha de paciente, no solo ofrecerlo como opción en el autocompletar.
  const [telefonoContacto, setTelefonoContacto] = useState('');
  // Bug real reportado por el usuario: el campo salía en blanco. `medplum.
  // getProfile()` puede no tener el perfil listo todavía en el primer render
  // (se resuelve async) — el inicializador de useState de arriba solo corre
  // UNA vez, así que si en ese primer render `practitionerId` todavía era ''
  // el teléfono recordado se perdía para siempre. Se resuelve en cuanto el
  // perfil esté listo, sin pisar lo que el médico ya haya tipeado.
  useEffect(() => {
    if (!practitionerId) return;
    const recordado = telefonosRecientes(practitionerId)[0];
    if (recordado) setTelefonoContacto((actual) => actual || recordado);
  }, [practitionerId]);
  const [signosAlarma, setSignosAlarma] = useState('');
  const [recomendaciones, setRecomendaciones] = useState('');

  const carritoTieneSustanciaControlada = items.some((it) => it.sustanciaControlada);

  const sugerencias = useMemo(
    () => (!catalogoEstado.cargando && catalogoEstado.error === null ? buscarEnCatalogo(catalogoEstado.medicamentos, busqueda) : []),
    [catalogoEstado, busqueda],
  );

  const dciActual = estado.fase === 'editando-item' ? estado.datos.dci : '';
  const nombreComercialActual = estado.fase === 'editando-item' ? estado.datos.nombreComercial : '';
  // Bug real reportado por el usuario: como el valor tipeado queda IGUAL a la
  // marca elegida, `marcasSugeridas` la sigue devolviendo (substring de sí
  // misma) — el popover se quedaba abierto para siempre después de
  // seleccionar, tapando el resto del formulario. Se cierra al elegir y se
  // vuelve a abrir en cuanto el médico vuelve a tipear.
  const [sugerenciasMarcaOcultas, setSugerenciasMarcaOcultas] = useState(false);
  const sugerenciasMarca = useMemo(
    () => (!catalogoEstado.cargando && catalogoEstado.error === null ? marcasSugeridas(catalogoEstado.medicamentos, dciActual, nombreComercialActual) : []),
    [catalogoEstado, dciActual, nombreComercialActual],
  );

  function abrirEdicion(datos: DatosClinicos, editIndex: number | null): void {
    setEstado({ fase: 'editando-item', datos, editIndex, cantidadEditadaAMano: editIndex !== null, error: null });
    setBusqueda('');
  }

  function elegirDelCatalogo(m: MedicamentoCatalogo): void {
    abrirEdicion(datosDeCatalogo(m), null);
  }

  function empezarManual(): void {
    abrirEdicion(datosDeCatalogo({ nombre: '', marca: '', forma: '', via: '', concentracion: '', dci: busqueda, cum: '', formaVenta: '' }), null);
  }

  function actualizarDatos(cambios: Partial<DatosClinicos>): void {
    if (estado.fase !== 'editando-item') return;
    const datos = { ...estado.datos, ...cambios };
    // Candado duro: con `controladaBloqueadaPorCatalogo` no hay forma de
    // apagar `sustanciaControlada` — ni por el checkbox (deshabilitado en el
    // render) ni por ningún otro camino que llegue acá.
    if (datos.controladaBloqueadaPorCatalogo) datos.sustanciaControlada = true;
    if (!estado.cantidadEditadaAMano && !('cantidadTotal' in cambios)) {
      datos.cantidadTotal = calcularCantidadTotal(datos);
    }
    setEstado({ ...estado, datos });
  }

  function guardarItem(): void {
    if (estado.fase !== 'editando-item') return;
    const { datos, editIndex } = estado;
    if (!datos.dci.trim()) return;
    if (datos.sustanciaControlada && !datos.numeroSerieRecetaEspecial.trim()) return;

    const otrosItems = items.filter((_, i) => i !== editIndex);
    if (datos.sustanciaControlada && otrosItems.length > 0) {
      setEstado({ ...estado, error: t('receta.avisoSustanciaControladaSola') });
      return;
    }
    if (!datos.sustanciaControlada && otrosItems.some((it) => it.sustanciaControlada)) {
      setEstado({ ...estado, error: t('receta.avisoNoAgregarConSustanciaControlada') });
      return;
    }

    setItems((prev) => (editIndex === null ? [...prev, datos] : prev.map((it, i) => (i === editIndex ? datos : it))));
    setEstado({ fase: 'carrito' });
  }

  function quitarItem(index: number): void {
    setItems((prev) => prev.filter((_, i) => i !== index));
  }

  async function finalizar(): Promise<void> {
    if (estado.fase !== 'firma-profesional' || !registroMsp.trim() || items.length === 0) return;
    try {
      const profile = medplum.getProfile();
      const paciente = await medplum.readReference<Patient>(subject);
      const pacienteNombre = paciente.name?.[0] ? formatHumanName(paciente.name[0]) : subject.display ?? t('consentimiento.pacienteGenerico');
      const pacienteEdad = edadEnAniosYMeses(paciente.birthDate);
      const pacienteSexo = paciente.gender ? t(`fichaPaciente.genero.${paciente.gender}`) : undefined;
      const diagnosticoPresuntivo = conCodigoCie(diagnostico) || undefined;

      // Checklist MSP/ACESS — antecedentes de alergias, ya registradas en
      // Medplum (mismo recurso que AlergiasTab.tsx): se traen automáticamente,
      // sin pedirle nada nuevo al médico. "No refiere" explícito si no hay
      // ninguna activa, nunca un campo vacío (el checklist exige el registro
      // explícito de ausencia, no un silencio ambiguo).
      const alergiasActivas = await medplum
        .searchResources('AllergyIntolerance', { patient: `Patient/${paciente.id}`, _sort: '-date', _count: 100 })
        .catch(() => [] as AllergyIntolerance[]);
      const nombresAlergias = alergiasActivas
        .filter((a) => a.clinicalStatus?.coding?.some((c) => c.code === 'active'))
        .map((a) => a.code?.text)
        .filter((texto): texto is string => Boolean(texto?.trim()));
      const alergias = nombresAlergias.length > 0 ? nombresAlergias.join(', ') : t('receta.sinAlergias');

      // Checklist MSP/ACESS — número correlativo (ya no aleatorio) + ciudad/
      // RUC/razón social del establecimiento, resueltos del lado del servidor.
      const preparacion = await prepararReceta();

      const medicamentosPdf: MedicamentoPdfReceta[] = items.map((datos) => {
        const cantidadNum = Number(datos.cantidadTotal);
        return {
          nombreMedicamento: nombreMedicamentoDe(datos),
          nombreComercial: datos.nombreComercial.trim() || undefined,
          viaAdministracion: datos.viaAdministracion.trim() || undefined,
          pauta: pautaDe(datos, t),
          cantidadTotal: datos.cantidadTotal ? `${datos.cantidadTotal} ${datos.dosisUnidad || ''}`.trim() : undefined,
          cantidadEnLetras: datos.cantidadTotal && Number.isInteger(cantidadNum) ? numeroALetras(cantidadNum) : undefined,
        };
      });

      // Invariante ya validada al agregar cada ítem (guardarItem): si hay más
      // de uno, ninguno es sustancia controlada — alcanza con mirar el primero.
      const esSustanciaControlada = carritoTieneSustanciaControlada;
      // El QR (receta común) tiene que existir ANTES de firmar — viaja
      // dentro del PDF firmado (2.8). La receta especial (sustancia
      // controlada) no usa QR propio: el documento oficial es el papel
      // pre-numerado de ACESS, el número de serie ya alcanza como
      // homologación (ver generarPdfReceta). Decisión legal, no técnica —
      // investigada y cerrada como intencional 2026-09-17 (ver el comentario
      // de `recetas.sustanciaControlada` en schema.ts, web/api/database):
      // Acuerdo 025-2020 Art. 7-9/20 exige que la receta especial sea el
      // papel que ACESS misma elabora y abastece al profesional, y la
      // declara "el único documento legal" — este sistema no puede emitir
      // un sustituto propio (PDF/QR) mientras ACESS no publique su propia
      // normativa de receta electrónica (Disposición General Cuarta).
      const qrToken = esSustanciaControlada ? undefined : nuevoQrToken();

      // Sin nombre/registro/fecha del prescriptor (2026-09-12): el servidor
      // arma el PDF y el QR con los de la sesión.
      const datosParaPdf: DatosRecetaSinFirma = {
        numeroReceta: preparacion.numero,
        // `razonSocial` (Datos del consultorio) reemplaza al nombre comercial
        // como "Nombre del establecimiento" impreso en la receta cuando está
        // cargado — checklist MSP/ACESS pide la razón social, no el nombre
        // comercial que usa el resto del sistema (WhatsApp, panel, etc.).
        consultorioNombre: preparacion.razonSocial || (tenantActual.estado === 'ok' ? tenantActual.tenant.clinicName : undefined),
        ciudadEmision: preparacion.ciudad || undefined,
        ruc: preparacion.ruc || undefined,
        direccionConsultorio: preparacion.direccion || undefined,
        telefonoConsultorio: preparacion.telefono || undefined,
        correoConsultorio: preparacion.correo || undefined,
        vigencia: vigencia.trim() || undefined,
        pacienteNombre,
        pacienteIdentificador: paciente.identifier?.[0]?.value,
        pacienteEdad,
        pacienteSexo,
        alergias,
        diagnosticoPresuntivo,
        practitionerTelefono: telefonoContacto.trim() || undefined,
        medicamentos: medicamentosPdf,
        sustanciaControlada: esSustanciaControlada,
        numeroSerieRecetaEspecial: esSustanciaControlada ? items[0]!.numeroSerieRecetaEspecial.trim() || undefined : undefined,
        signosAlarma: signosAlarma.trim() || undefined,
        recomendacionesNoFarmacologicas: recomendaciones.trim() || undefined,
      };
      // La receta completa va embebida en el FRAGMENTO del QR (`#r=...`) —
      // "Receta digital", decisión #1. Desde el 2026-09-12 el servidor arma
      // el PDF, el QR y su sello SHA-256 (que guarda él mismo para
      // `emitirReceta`), con nombre/matrícula/fecha de la sesión.
      setEstado({ fase: 'procesando', mensaje: t('receta.firmando') });
      const documentoId = qrToken ? `receta/${qrToken}` : `receta-especial/${crypto.randomUUID()}`;
      const firmado = await firmarDocumentoEnServidor({
        documentoId,
        tipoDocumento: 'receta',
        especificacion: { modo: 'receta', datos: datosParaPdf, qrToken },
      });
      const urlValidacion = firmado.urlValidacion;
      const binarioPdfFirmado = await medplum.createBinary({
        data: firmado.signedPdf,
        contentType: 'application/pdf',
        filename: 'receta-firmada.pdf',
        securityContext: subject, // MT-042: el paciente, para la regla de vínculo asistencial
      });
      const docRefFirma = await medplum.createResource<DocumentReference>({
        resourceType: 'DocumentReference',
        status: 'current',
        subject,
        type: { coding: [{ system: 'http://loinc.org', code: '57833-6', display: 'Prescription record' }] },
        author: profile ? [{ reference: `${profile.resourceType}/${profile.id}` }] : [],
        content: [
          {
            attachment: {
              contentType: 'application/pdf',
              url: `Binary/${binarioPdfFirmado.id}`,
              title: firmado.simulada ? 'Receta — FIRMA SIMULADA (Security Data no configurado)' : 'Receta firmada (Security Data)',
            },
          },
        ],
        extension: [
          { url: 'http://logiacare.ec/fhir/extension/firma-signature-ref', valueString: firmado.signatureRef },
          { url: 'http://logiacare.ec/fhir/extension/firma-firmado-en', valueDateTime: firmado.firmadoEn },
          { url: 'http://logiacare.ec/fhir/extension/firma-simulada', valueBoolean: firmado.simulada },
        ],
      });

      setEstado({ fase: 'procesando', mensaje: t('receta.guardando') });
      const mrs: MedicationRequest[] = [];
      for (const datos of items) {
        const mr = await construirReceta({
          medplum,
          subject,
          encounter,
          dci: datos.dci,
          nombreComercial: datos.nombreComercial || undefined,
          formaFarmaceutica: datos.formaFarmaceutica || undefined,
          pauta: pautaDe(datos, t),
          cantidadTotal: datos.cantidadTotal ? Number(datos.cantidadTotal) : undefined,
          unidadCantidad: datos.dosisUnidad || undefined,
          sustanciaControlada: datos.sustanciaControlada,
          numeroSerieRecetaEspecial: datos.numeroSerieRecetaEspecial.trim() || undefined,
          documentReferenceFirma: { reference: `DocumentReference/${docRefFirma.id}` },
        });
        mrs.push(mr);
      }

      if (qrToken) {
        await emitirReceta({
          qrToken,
          medicationRequestRefs: mrs.map((mr) => `MedicationRequest/${mr.id}`),
          medicamentoDisplay: medicamentosPdf.map((m) => m.nombreMedicamento).join('; '),
          sustanciaControlada: false,
          signatureRef: firmado.signatureRef,
        });
      }

      recordarTelefono(practitionerId, telefonoContacto);
      const blob = new Blob([firmado.signedPdf as BlobPart], { type: 'application/pdf' });
      setEstado({ fase: 'listo', url: URL.createObjectURL(blob), pdf: firmado.signedPdf, link: urlValidacion, simulada: firmado.simulada, mrs, vias: items.map((datos) => datos.viaAdministracion.trim()) });
    } catch (err) {
      setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
    }
  }

  const paso = pasoActivo(estado.fase);
  const pasos = [t('receta.pasoCatalogo'), t('receta.pasoDatos'), t('receta.pasoFirma')];

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-base font-medium text-hce-ink">{t('receta.tituloWizard')}</h4>
      <p className="text-xs font-mono uppercase tracking-wide text-hce-muted">
        {pasos.map((p, i) => (
          <span key={p} className={cn(i === paso && 'font-semibold text-hce-ink')}>
            {i > 0 && ' → '}
            {p}
          </span>
        ))}
      </p>

      {estado.fase === 'carrito' && (
        <div className="flex flex-col gap-3">
          <SelectorDiagnosticoCie10 label={t('receta.diagnosticoPresuntivo')} value={diagnostico} onChange={setDiagnostico} />

          <div className="flex flex-col gap-1">
            <Label>{t('receta.medicamentosEnLaReceta')}</Label>
            {items.length === 0 ? (
              <p className="text-sm text-hce-muted">{t('receta.sinMedicamentosAgregados')}</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {items.map((datos, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 rounded-md border border-border p-2">
                    <div className="flex flex-col">
                      <span className="text-sm text-hce-ink">{nombreMedicamentoDe(datos)}</span>
                      {pautaDe(datos, t) && <span className="text-xs text-hce-muted">{pautaDe(datos, t)}</span>}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => abrirEdicion(datos, i)}>
                        {t('receta.editarMedicamento')}
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => quitarItem(i)}>
                        {t('receta.quitarMedicamento')}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {carritoTieneSustanciaControlada ? (
            <Alert variant="orange">
              <AlertDescription>{t('receta.avisoSustanciaControladaSola')}</AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-col gap-1">
              <Label htmlFor="receta-buscar">{t('receta.agregarMedicamento')}</Label>
              {catalogoEstado.cargando && (
                <div className="flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  <p className="text-sm text-hce-muted">{t('receta.cargandoCatalogo')}</p>
                </div>
              )}
              {!catalogoEstado.cargando && catalogoEstado.error && (
                <Alert variant="orange">
                  <AlertTitle>{t('receta.catalogoNoDisponible')}</AlertTitle>
                  <AlertDescription>{t('receta.catalogoNoDisponibleDetalle')}</AlertDescription>
                </Alert>
              )}
              <Popover open={sugerencias.length > 0} onOpenChange={() => {}}>
                <PopoverAnchor asChild>
                  <Input
                    id="receta-buscar"
                    placeholder={t('receta.buscarMedicamentoPlaceholder')}
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    autoComplete="off"
                  />
                </PopoverAnchor>
                <PopoverContent className="w-[--radix-popover-anchor-width] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                  <Command shouldFilter={false}>
                    <CommandList>
                      <CommandEmpty>{t('receta.ingresarManual')}</CommandEmpty>
                      <CommandGroup>
                        {sugerencias.map((m, i) => (
                          <CommandItem key={`${m.dci}-${m.nombre}-${i}`} value={`${m.dci} — ${m.nombre}`} onSelect={() => elegirDelCatalogo(m)}>
                            {m.dci} — {m.nombre}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <Button type="button" size="sm" variant="outline" className="w-fit" onClick={empezarManual} disabled={!busqueda.trim()}>
                {t('receta.ingresarManual')}
              </Button>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={onCancelar}>
              {t('receta.cancelar')}
            </Button>
            <Button
              type="button"
              onClick={() => setEstado({ fase: 'firma-profesional' })}
              disabled={items.length === 0 || !diagnostico.texto.trim()}
            >
              {t('receta.continuarAFirma')}
            </Button>
          </div>
        </div>
      )}

      {estado.fase === 'editando-item' && (
        <div className="flex flex-col gap-3">
          {estado.error && (
            <Alert variant="red">
              <AlertDescription>{estado.error}</AlertDescription>
            </Alert>
          )}
          <div className="flex flex-col gap-1">
            <Label htmlFor="receta-dci">{t('formularios.f005.dci')}</Label>
            <Input id="receta-dci" value={estado.datos.dci} onChange={(e) => actualizarDatos({ dci: e.target.value })} required />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="receta-nombre-comercial">{t('formularios.f005.nombreComercial')}</Label>
            <Popover open={sugerenciasMarca.length > 0 && !sugerenciasMarcaOcultas} onOpenChange={() => {}}>
              <PopoverAnchor asChild>
                <Input
                  id="receta-nombre-comercial"
                  value={estado.datos.nombreComercial}
                  onChange={(e) => {
                    setSugerenciasMarcaOcultas(false);
                    actualizarDatos({ nombreComercial: e.target.value });
                  }}
                  autoComplete="off"
                />
              </PopoverAnchor>
              <PopoverContent className="w-[--radix-popover-anchor-width] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                <Command shouldFilter={false}>
                  <CommandList>
                    <CommandGroup>
                      {sugerenciasMarca.map((marca) => (
                        <CommandItem
                          key={marca}
                          value={marca}
                          onSelect={() => {
                            actualizarDatos({ nombreComercial: marca });
                            setSugerenciasMarcaOcultas(true);
                          }}
                        >
                          {marca}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1">
              <Label htmlFor="receta-forma">{t('formularios.f005.forma')}</Label>
              <Input id="receta-forma" value={estado.datos.formaFarmaceutica} onChange={(e) => actualizarDatos({ formaFarmaceutica: e.target.value })} />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <Label htmlFor="receta-concentracion">{t('formularios.f005.concentracion')}</Label>
              <Input id="receta-concentracion" value={estado.datos.concentracion} onChange={(e) => actualizarDatos({ concentracion: e.target.value })} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="receta-via-administracion">{t('receta.viaAdministracion')}</Label>
            <Input
              id="receta-via-administracion"
              list="receta-vias-administracion-sugeridas"
              placeholder={t('receta.viaAdministracionPlaceholder')}
              value={estado.datos.viaAdministracion}
              onChange={(e) => actualizarDatos({ viaAdministracion: e.target.value })}
            />
            <datalist id="receta-vias-administracion-sugeridas">
              {VIAS_ADMINISTRACION_SUGERIDAS.map((via) => (
                <option key={via} value={via} aria-label={via} />
              ))}
            </datalist>
          </div>

          <h6 className="text-sm font-medium text-hce-ink">{t('receta.pautaTitulo')}</h6>
          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1">
              <Label htmlFor="receta-dosis-cantidad">{t('receta.dosisCantidad')}</Label>
              <Input id="receta-dosis-cantidad" value={estado.datos.dosisCantidad} onChange={(e) => actualizarDatos({ dosisCantidad: e.target.value })} />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <Label htmlFor="receta-dosis-unidad">{t('receta.dosisUnidad')}</Label>
              <Input
                id="receta-dosis-unidad"
                list="receta-unidades-dosis-sugeridas"
                placeholder={t('receta.dosisUnidadPlaceholder')}
                value={estado.datos.dosisUnidad}
                onChange={(e) => actualizarDatos({ dosisUnidad: e.target.value })}
              />
              <datalist id="receta-unidades-dosis-sugeridas">
                {UNIDADES_DOSIS_SUGERIDAS.map((unidad) => (
                  <option key={unidad} value={unidad} aria-label={unidad} />
                ))}
              </datalist>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex flex-1 flex-col gap-1">
              <Label htmlFor="receta-frecuencia">{t('receta.frecuenciaHoras')}</Label>
              <Input id="receta-frecuencia" value={estado.datos.frecuenciaHoras} onChange={(e) => actualizarDatos({ frecuenciaHoras: e.target.value })} />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              <Label htmlFor="receta-duracion">{t('receta.duracionDias')}</Label>
              <Input id="receta-duracion" value={estado.datos.duracionDias} onChange={(e) => actualizarDatos({ duracionDias: e.target.value })} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="receta-cantidad-total">{t('receta.cantidadTotal')}</Label>
            <Input
              id="receta-cantidad-total"
              value={estado.datos.cantidadTotal}
              onChange={(e) => setEstado({ ...estado, cantidadEditadaAMano: true, datos: { ...estado.datos, cantidadTotal: e.target.value } })}
            />
            <p className="text-xs text-hce-muted">{t('receta.cantidadTotalDescripcion')}</p>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="receta-sustancia-controlada"
              checked={estado.datos.sustanciaControlada}
              disabled={estado.datos.controladaBloqueadaPorCatalogo}
              onCheckedChange={(checked) => actualizarDatos({ sustanciaControlada: checked === true })}
            />
            <Label htmlFor="receta-sustancia-controlada">{t('receta.sustanciaControlada')}</Label>
          </div>
          {estado.datos.controladaBloqueadaPorCatalogo && (
            <Alert variant="red">
              <AlertTitle>{t('receta.sustanciaControladaBloqueadaTitulo')}</AlertTitle>
              <AlertDescription>{t('receta.sustanciaControladaBloqueadaDescripcion')}</AlertDescription>
            </Alert>
          )}
          {estado.datos.sustanciaControlada && (
            <div className="flex flex-col gap-1">
              <Label htmlFor="receta-numero-serie">{t('receta.numeroSerieAcess')}</Label>
              <Input
                id="receta-numero-serie"
                value={estado.datos.numeroSerieRecetaEspecial}
                onChange={(e) => actualizarDatos({ numeroSerieRecetaEspecial: e.target.value })}
                required
              />
              <p className="text-xs text-hce-muted">{t('receta.numeroSerieAcessDescripcion')}</p>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={() => setEstado({ fase: 'carrito' })}>
              {t('receta.cancelar')}
            </Button>
            <Button
              type="button"
              onClick={guardarItem}
              disabled={!estado.datos.dci.trim() || (estado.datos.sustanciaControlada && !estado.datos.numeroSerieRecetaEspecial.trim())}
            >
              {estado.editIndex === null ? t('receta.agregarALaReceta') : t('receta.guardarCambiosMedicamento')}
            </Button>
          </div>
        </div>
      )}

      {estado.fase === 'firma-profesional' && (
        <div className="flex max-w-[420px] flex-col gap-3">
          <p className="text-sm text-hce-muted">{t('receta.instruccionFirmaProfesional')}</p>
          <div className="flex flex-col gap-1">
            <Label htmlFor="receta-vigencia">{t('receta.vigencia')}</Label>
            <Select value={vigenciaId} onValueChange={(v) => setVigenciaId(v as typeof vigenciaId)}>
              <SelectTrigger id="receta-vigencia">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OPCIONES_VIGENCIA_RECETA.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.texto}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="receta-registro-msp">{t('consentimiento.registroMsp')}</Label>
            <Input
              id="receta-registro-msp"
              value={registroMsp}
              readOnly
              placeholder={rolEstado.cargando ? t('formulario.firma.cargandoMatricula') : ''}
            />
            {!rolEstado.cargando && !registroMsp && (
              <Alert variant="red">
                <AlertDescription>{t('formulario.firma.sinMatricula')}</AlertDescription>
              </Alert>
            )}
          </div>

          <h6 className="text-sm font-medium text-hce-ink">{t('receta.indicacionesTitulo')}</h6>
          <div className="flex flex-col gap-1">
            <Label htmlFor="receta-telefono-contacto">{t('receta.telefonoContacto')}</Label>
            <Input
              id="receta-telefono-contacto"
              list="receta-telefonos-recientes"
              value={telefonoContacto}
              onChange={(e) => setTelefonoContacto(e.target.value)}
            />
            {/* Autocompletado nativo del navegador — el sistema recuerda los
                últimos teléfonos que cargó el médico (telefonosPrescriptorRecientes.ts,
                localStorage) y los ofrece acá, pedido explícito del usuario. */}
            <datalist id="receta-telefonos-recientes">
              {telefonosRecientes(practitionerId).map((tel) => (
                <option key={tel} value={tel} aria-label={tel} />
              ))}
            </datalist>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="receta-signos-alarma">{t('receta.signosAlarma')}</Label>
            <Textarea id="receta-signos-alarma" value={signosAlarma} onChange={(e) => setSignosAlarma(e.target.value)} rows={2} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="receta-recomendaciones">{t('receta.recomendacionesNoFarmacologicas')}</Label>
            <Textarea id="receta-recomendaciones" value={recomendaciones} onChange={(e) => setRecomendaciones(e.target.value)} rows={2} />
          </div>

          <Button type="button" onClick={() => void finalizar()} disabled={!registroMsp.trim()}>
            {t('consentimiento.firmarYGuardar')}
          </Button>
        </div>
      )}

      {estado.fase === 'procesando' && (
        <div className="flex flex-col items-center gap-2 py-4">
          <Loader2 className="size-5 animate-spin" />
          <p className="text-sm text-hce-muted">{estado.mensaje}</p>
        </div>
      )}

      {estado.fase === 'listo' && (
        <div className="flex flex-col gap-2">
          <Alert>
            <AlertTitle>{t('formulario.firma.listoTitulo')}</AlertTitle>
            <AlertDescription>{estado.simulada ? t('formulario.firma.listoSimulada') : t('formulario.firma.listoDetalle')}</AlertDescription>
          </Alert>
          {!rolEstado.cargando && rolEstado.farmaciaInternaActiva && <p className="text-xs text-hce-muted">{t('receta.disponibleEnFarmaciaInterna')}</p>}
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" className="w-fit" onClick={() => setMostrarPreview((v) => !v)}>
              {t('formulario.firma.verImprimir')}
            </Button>
            <Button asChild variant="outline" size="sm" className="w-fit">
              <a href={estado.url} download="receta.pdf">
                {t('formulario.firma.descargar')}
              </a>
            </Button>
          </div>
          {mostrarPreview && <iframe src={estado.url} title={t('formulario.firma.verImprimir')} className="h-[70vh] w-full rounded-md border border-border" />}
          <EnvioDigitalDocumento subject={subject} pdf={estado.pdf} fileName="receta.pdf" etiquetaDocumento={t('receta.etiquetaDocumento')} link={estado.link} />
          <Button type="button" size="sm" className="w-fit" onClick={() => onCreada(estado.mrs, estado.vias)}>
            {t('receta.cerrar')}
          </Button>
        </div>
      )}

      {estado.fase === 'error' && (
        <Alert variant="red">
          <AlertTitle>{t('receta.error')}</AlertTitle>
          <AlertDescription>{estado.mensaje}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
